/**
 * PhenotypeExplorerPage.jsx — iter901 (Feb 2026)
 *
 * Interactive visualization showing how a condition/phenotype
 *   condition → grouped KEGG pathways → 6 breast tissue zones →
 *   Digital Twins.
 *
 * Includes a compare-mode (pick 2 conditions to see shared / unique
 * pathways side-by-side).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, GitBranch, Layers, Activity, Users,
  ChevronDown, GitCompareArrows, Sparkles,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import EducationalRibbon from "@/components/EducationalRibbon";

const ZONE_META = {
  epithelium: { label_bg: "Епителна тъкан",       color: "from-pink-500 to-rose-500" },
  stroma:     { label_bg: "Строма",                color: "from-emerald-500 to-teal-500" },
  adipose:    { label_bg: "Мастна тъкан",          color: "from-amber-500 to-orange-500" },
  immune:     { label_bg: "Имунна ниша",           color: "from-indigo-500 to-violet-500" },
  vascular:   { label_bg: "Съдова тъкан",          color: "from-red-500 to-rose-600" },
  lymphatic:  { label_bg: "Лимфна тъкан",          color: "from-sky-500 to-cyan-500" },
};

const GROUP_COLORS = {
  "Хормонални":              "from-pink-500 to-fuchsia-500",
  "Метаболитни":             "from-amber-500 to-orange-500",
  "Съдови":                  "from-red-500 to-rose-500",
  "Възпалителни / Имунни":   "from-indigo-500 to-purple-500",
  "Клетъчен цикъл":          "from-violet-500 to-purple-600",
  "Оксидативни / Детокс":    "from-emerald-500 to-teal-500",
  "Структурни / ECM":        "from-lime-500 to-emerald-500",
  "Генетични / Хромозомни":  "from-slate-500 to-slate-700",
  "Желязо / Хематопоеза":    "from-rose-500 to-red-600",
  "Клетъчен катаболизъм":    "from-orange-500 to-amber-600",
  "Other":                   "from-gray-500 to-gray-600",
};

const CATEGORY_LABEL = {
  hormonal:  "Хормонални",
  metabolic: "Метаболитни",
  genetic:   "Генетични",
  nutrition: "Хранителни дефицити",
  biomarker: "Биомаркер",
  cardio:    "Сърдечно-съдови",
  autoimmune:"Автоимунни",
  benign:    "Доброкачествени",
  cancer:    "Онкологични",
  other:     "Други",
};

export default function PhenotypeExplorerPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [conditions, setConditions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [compareWith, setCompareWith] = useState(null);
  const [data, setData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("explore");  // "explore" | "compare"

  // Load available conditions
  useEffect(() => {
    axios.get(`${API}/phenotype-explorer/conditions`)
      .then(res => {
        setConditions(res.data.conditions || []);
        // Default: pcos_a (nice showcase)
        const def = (res.data.conditions || []).find(c => c.id === "pcos_a") || res.data.conditions?.[0];
        if (def) setSelected(def.id);
      })
      .catch(() => setConditions([]));
  }, [API]);

  const load = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      if (mode === "compare" && compareWith) {
        const { data } = await axios.get(`${API}/phenotype-explorer/compare/${selected}/${compareWith}`);
        setCompareData(data);
        setData(data.a);
      } else {
        const { data } = await axios.get(`${API}/phenotype-explorer/${selected}`);
        setData(data);
        setCompareData(null);
      }
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API, selected, compareWith, mode]);

  useEffect(() => { load(); }, [load]);

  // Group conditions by category for dropdown
  const groupedConditions = useMemo(() => {
    const grouped = {};
    conditions.forEach(c => {
      const cat = CATEGORY_LABEL[c.category] || c.category;
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(c);
    });
    return grouped;
  }, [conditions]);

  const bg = isDark ? "bg-[#0a0a12] text-gray-100" : "bg-slate-50 text-gray-900";

  return (
    <div className={`min-h-screen ${bg}`} data-testid="phenotype-explorer-page">
      <SEO title="Phenotype Explorer — Nutramilo" description="Интерактивна визуализация на фенотипи, KEGG пътища, тъкани и Digital Twins." />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <button
          onClick={() => navigate(-1)}
          className={`inline-flex items-center gap-1 text-xs font-semibold mb-3 opacity-70 hover:opacity-100 ${isDark ? "text-slate-300" : "text-slate-600"}`}
          data-testid="phenotype-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Назад
        </button>

        <EducationalRibbon lang="bg" isDark={isDark} variant="banner" className="mb-4" />

        <div className="flex items-start gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pink-500 to-violet-600 grid place-items-center shrink-0">
            <GitBranch className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold">Phenotype Explorer</h1>
            <p className={`text-xs sm:text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              Как един фенотип активира конкретни KEGG пътища → тъканни зони → Digital Twins.
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className={`rounded-2xl border p-4 mb-4 ${isDark ? "border-slate-700 bg-slate-900/50" : "border-slate-200 bg-white"}`}>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setMode("explore"); setCompareWith(null); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                  mode === "explore"
                    ? "bg-gradient-to-r from-pink-500 to-violet-600 text-white"
                    : isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
                }`}
                data-testid="mode-explore"
              >
                <Layers className="inline w-3 h-3 mr-1" /> Разгледай
              </button>
              <button
                onClick={() => setMode("compare")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                  mode === "compare"
                    ? "bg-gradient-to-r from-pink-500 to-violet-600 text-white"
                    : isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
                }`}
                data-testid="mode-compare"
              >
                <GitCompareArrows className="inline w-3 h-3 mr-1" /> Сравни
              </button>
            </div>

            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-0">
              <ConditionSelector
                value={selected}
                onChange={setSelected}
                grouped={groupedConditions}
                isDark={isDark}
                testid="condition-select-a"
                label="Фенотип A"
              />
              {mode === "compare" && (
                <ConditionSelector
                  value={compareWith}
                  onChange={setCompareWith}
                  grouped={groupedConditions}
                  isDark={isDark}
                  testid="condition-select-b"
                  label="Фенотип B"
                />
              )}
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin opacity-40" />
          </div>
        )}

        {!loading && data && mode === "explore" && (
          <SingleExplorer data={data} isDark={isDark} onPickRelated={setSelected} />
        )}

        {!loading && compareData && mode === "compare" && (
          <CompareView data={compareData} isDark={isDark} />
        )}
      </div>
    </div>
  );
}


function ConditionSelector({ value, onChange, grouped, isDark, testid, label }) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>{label}</span>
      <div className="relative">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={`w-full appearance-none rounded-lg px-3 py-2 text-sm font-medium border ${
            isDark ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"
          }`}
        >
          <option value="">— избери —</option>
          {Object.entries(grouped).map(([cat, items]) => (
            <optgroup key={cat} label={cat}>
              {items.map(c => (
                <option key={c.id} value={c.id}>{c.name_bg} · {c.pathway_count}p</option>
              ))}
            </optgroup>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 pointer-events-none" />
      </div>
    </label>
  );
}


function SingleExplorer({ data, isDark, onPickRelated }) {
  const cardCls = isDark ? "border-slate-700 bg-slate-900/50" : "border-slate-200 bg-white";
  return (
    <div className="space-y-4" data-testid="single-explorer">
      {/* Header card */}
      <div className={`rounded-2xl border p-5 ${cardCls}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-extrabold" data-testid="phenotype-title">{data.name_bg}</h2>
            <div className="text-xs opacity-60 mt-1">
              <code>{data.condition_id}</code> · {data.pathway_count} pathways · {data.zones.length} тъканни зони · {data.twins.length} twins
            </div>
          </div>
          <span className={`text-[11px] px-2 py-1 rounded-full ${
            isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
          }`}>{data.category}</span>
        </div>
      </div>

      {/* Three-column flow: Pathway Groups → Tissue Zones → Twins */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Column 1 — Pathway groups */}
        <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid="col-pathways">
          <div className="flex items-center gap-2 mb-3">
            <GitBranch className="w-4 h-4 text-pink-500" />
            <h3 className="font-bold text-sm">1. KEGG Pathway групи</h3>
          </div>
          <div className="space-y-2">
            {data.groups.map(g => {
              const color = GROUP_COLORS[g.name] || GROUP_COLORS.Other;
              return (
                <div key={g.name} className={`rounded-lg p-2 border ${
                  isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"
                }`} data-testid={`pathway-group-${g.name}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${color}`} />
                    <span className="text-xs font-bold">{g.name}</span>
                    <span className="text-[10px] opacity-60 ml-auto">{g.pathways.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {g.pathways.map(p => (
                      <span
                        key={p.id}
                        title={`${p.name_bg}${p.kegg ? ` (${p.kegg})` : ""}`}
                        className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                          isDark ? "bg-slate-700 text-slate-200" : "bg-white text-slate-600 border border-slate-200"
                        }`}
                      >
                        {p.id.length > 24 ? p.id.slice(0, 24) + "…" : p.id}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Column 2 — Tissue zones */}
        <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid="col-zones">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-emerald-500" />
            <h3 className="font-bold text-sm">2. Активирани тъканни зони</h3>
          </div>
          <div className="space-y-2">
            {data.zones.length === 0 && (
              <div className="text-xs opacity-60">Няма директни активирани зони.</div>
            )}
            {data.zones.map(z => {
              const meta = ZONE_META[z.id] || { label_bg: z.id, color: "from-slate-500 to-slate-600" };
              return (
                <div key={z.id} className={`rounded-lg p-2.5 border ${
                  isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"
                }`} data-testid={`zone-${z.id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className={`w-4 h-4 rounded bg-gradient-to-r ${meta.color}`} />
                    <span className="text-xs font-bold">{meta.label_bg}</span>
                  </div>
                  <p className="text-[10px] opacity-70 leading-snug">{z.summary_bg}</p>
                  {z.markers?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {z.markers.slice(0, 4).map((m, i) => (
                        <span key={i} className={`text-[9px] px-1 py-0.5 rounded ${
                          isDark ? "bg-slate-700 text-slate-300" : "bg-white text-slate-600 border border-slate-200"
                        }`}>{m}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Column 3 — Twins */}
        <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid="col-twins">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-violet-500" />
            <h3 className="font-bold text-sm">3. Активирани Digital Twins</h3>
          </div>
          <div className="space-y-2">
            {data.twins.map(t => (
              <div key={t.id} className={`rounded-lg p-2 border flex items-center gap-2 ${
                isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"
              }`} data-testid={`twin-${t.id}`}>
                <div className={`w-6 h-6 rounded bg-${t.color || "slate"}-500/20 grid place-items-center`}>
                  <Sparkles className="w-3 h-3 opacity-70" />
                </div>
                <div>
                  <div className="text-xs font-semibold">{t.label_bg}</div>
                  <code className="text-[9px] opacity-50">{t.id}</code>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Related conditions */}
      {data.related?.length > 0 && (
        <div className={`rounded-2xl border p-4 ${cardCls}`}>
          <div className="flex items-center gap-2 mb-2">
            <GitCompareArrows className="w-4 h-4 text-amber-500" />
            <h3 className="font-bold text-sm">Свързани фенотипи (споделят ≥ 3 pathways)</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.related.map(r => (
              <button
                key={r.condition_id}
                onClick={() => onPickRelated(r.condition_id)}
                data-testid={`related-${r.condition_id}`}
                className={`text-left rounded-lg p-2 border transition-all hover:scale-[1.02] ${
                  isDark ? "border-slate-700 bg-slate-800/40 hover:bg-slate-800" : "border-slate-200 bg-slate-50 hover:bg-white"
                }`}
              >
                <div className="text-xs font-semibold">{r.name_bg}</div>
                <div className="text-[10px] opacity-60">{r.shared_count} общи pathways</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function CompareView({ data, isDark }) {
  const cardCls = isDark ? "border-slate-700 bg-slate-900/50" : "border-slate-200 bg-white";
  return (
    <div className="space-y-4" data-testid="compare-view">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`rounded-2xl border p-4 ${cardCls}`}>
          <h3 className="font-bold text-sm mb-3">🅰 {data.a.name_bg}</h3>
          <div className="text-xs opacity-70 mb-2">{data.a.pathway_count} pathways</div>
          <div className="flex flex-wrap gap-1">
            {data.only_a.map(p => (
              <span key={p.id} className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                isDark ? "bg-pink-500/20 text-pink-300" : "bg-pink-100 text-pink-700"
              }`} title={p.name_bg} data-testid={`only-a-${p.id}`}>
                {p.id}
              </span>
            ))}
            {data.only_a.length === 0 && (
              <span className="text-xs opacity-60">Няма уникални pathways за A.</span>
            )}
          </div>
        </div>

        <div className={`rounded-2xl border p-4 ${cardCls}`}>
          <h3 className="font-bold text-sm mb-3">🅱 {data.b.name_bg}</h3>
          <div className="text-xs opacity-70 mb-2">{data.b.pathway_count} pathways</div>
          <div className="flex flex-wrap gap-1">
            {data.only_b.map(p => (
              <span key={p.id} className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"
              }`} title={p.name_bg} data-testid={`only-b-${p.id}`}>
                {p.id}
              </span>
            ))}
            {data.only_b.length === 0 && (
              <span className="text-xs opacity-60">Няма уникални pathways за B (изцяло се съдържа в A).</span>
            )}
          </div>
        </div>
      </div>

      <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid="shared-pathways">
        <h3 className="font-bold text-sm mb-3">🔗 Общи pathways ({data.shared.length})</h3>
        <div className="flex flex-wrap gap-1">
          {data.shared.map(p => (
            <span key={p.id} className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
              isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"
            }`} title={p.name_bg}>
              {p.id}
            </span>
          ))}
          {data.shared.length === 0 && (
            <span className="text-xs opacity-60">Няма общи pathways.</span>
          )}
        </div>
      </div>
    </div>
  );
}
